<script setup>
import { computed, watch } from 'vue';
import { Handle, Position, useVueFlow, useNode } from '@vue-flow/core';
import { useI18n } from 'vue-i18n';
import { useNodeAudience } from '../../composables/useNodeAudience.js';

const props = defineProps({
  data: { type: Object, default: () => ({}) },
  selected: { type: Boolean, default: false },
});

const { t } = useI18n();
const { updateNodeInternals } = useVueFlow();
const { id } = useNode();
const audienceCount = useNodeAudience(id);

const teams = computed(() => (props.data.teams ?? []).filter(Boolean));

watch(teams, () => updateNodeInternals(id));

function handleLeft(index, total) {
  return `${((index + 1) / (total + 1)) * 100}%`;
}
</script>

<template>
  <div
    class="flow-node w-[180px] rounded-xl shadow-md border-2 transition-all"
    :class="selected ? 'border-n-teal-8' : 'border-transparent'"
  >
    <Handle type="target" :position="Position.Top" />

    <div class="flex items-center gap-2 px-3 py-2 bg-n-teal-9 text-white rounded-t-xl">
      <span class="i-lucide-split size-4 shrink-0" />
      <span class="text-xs font-semibold truncate flex-1">{{ data.name || t('FLOW_BUILDER.NODES.CLASSIFY.TITLE') }}</span>
      <span v-if="audienceCount !== null" class="text-[9px] bg-white/20 rounded-full px-1.5 py-0.5 font-medium tabular-nums shrink-0">
        {{ audienceCount }}
      </span>
    </div>

    <div class="px-3 py-2 bg-n-solid-1 text-n-slate-12 text-xs truncate" :class="teams.length ? '' : 'rounded-b-xl'">
      {{ data.assistant_name || t('FLOW_BUILDER.NODES.CLASSIFY.SELECT_ASSISTANT') }}
    </div>

    <div
      v-if="teams.length"
      class="relative bg-n-solid-1 rounded-b-xl pb-3 pt-1"
      :style="{ minHeight: '28px' }"
    >
      <span
        v-for="(team, i) in teams"
        :key="team.id"
        class="absolute text-[9px] text-n-slate-9 truncate max-w-[60px] text-center -translate-x-1/2"
        :style="{ left: handleLeft(i, teams.length), bottom: '10px' }"
      >
        {{ team.name }}
      </span>
    </div>

    <Handle
      v-for="(team, i) in teams"
      :id="String(team.id)"
      :key="team.id"
      type="source"
      :position="Position.Bottom"
      class="!bg-n-teal-9 !border-n-teal-7"
      :style="{ left: handleLeft(i, teams.length) }"
    />
  </div>
</template>
